import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Row,
  Col,
  Typography,
  DatePicker,
  Tooltip,
  Divider,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getAllStock,
  receiveStock,
  getAllRawMaterials,
  getAllMovements,
} from "../../api/inventoryApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const QuickStockEntry = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  // Stock grouped view
  const [stockData, setStockData] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState(undefined);

  // Recent movements (RECEIPT type)
  const [movements, setMovements] = useState([]);
  const [movLoading, setMovLoading] = useState(false);
  const [movPagination, setMovPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [dateRange, setDateRange] = useState(null);

  // Add-stock modal
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Raw materials lookup
  const [materials, setMaterials] = useState([]);
  const [matLoading, setMatLoading] = useState(false);

  // Watch selected material to show reel width for paper
  const selectedMaterialId = Form.useWatch("material_id", form);
  const selectedMaterial = materials.find((m) => m.id === selectedMaterialId);
  const isPaperMaterial = selectedMaterial?.category === "PAPER";

  // Watch reel fields for empirical weight calculation
  const watchReelWidth = Form.useWatch("reel_width", form);
  const watchActualReelWeight = Form.useWatch("actual_reel_weight", form);
  const watchQuantity = Form.useWatch("quantity", form);

  // Empirical weight per reel = reel_width × 14
  const empiricalWeightPerReel = watchReelWidth ? watchReelWidth * 14 : 0;
  // Effective weight per reel: use actual if provided, else empirical
  const effectiveWeightPerReel = watchActualReelWeight || empiricalWeightPerReel;
  // Total weight for all reels
  const totalReelWeight = isPaperMaterial && watchQuantity ? effectiveWeightPerReel * watchQuantity : 0;

  // ── Fetch stock summary ────────────────────────────────────
  const fetchStock = useCallback(async () => {
    setStockLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      const result = await getAllStock(params);
      setStockData(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error(error.message);
      toast.error("Failed to load stock data");
    } finally {
      setStockLoading(false);
    }
  }, [filterCategory]);

  // ── Fetch recent receipt movements ─────────────────────────
  const fetchMovements = useCallback(async (page = 1, limit = 20) => {
    setMovLoading(true);
    try {
      const params = { page, limit, movement_type: "RECEIPT" };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.from_date = dateRange[0].format("YYYY-MM-DD");
        params.to_date = dateRange[1].format("YYYY-MM-DD");
      }
      const result = await getAllMovements(params);
      setMovements(result?.data || []);
      const pg = result?.pagination || {};
      setMovPagination({
        current: pg.page || page,
        pageSize: pg.limit || limit,
        total: pg.total || 0,
      });
    } catch (error) {
      console.error(error.message);
    } finally {
      setMovLoading(false);
    }
  }, [dateRange]);

  // ── Fetch raw materials for dropdown ───────────────────────
  const fetchMaterials = useCallback(async () => {
    setMatLoading(true);
    try {
      const result = await getAllRawMaterials({ limit: 500, active_only: "true" });
      setMaterials(result?.data || []);
    } catch (error) {
      console.error(error.message);
    } finally {
      setMatLoading(false);
    }
  }, []);

  useEffect(() => { fetchStock(); }, [fetchStock]);
  useEffect(() => { fetchMovements(); }, [fetchMovements]);
  useEffect(() => { fetchMaterials(); }, [fetchMaterials]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Handlers ───────────────────────────────────────────────
  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({ unit: "kg" });
    setModalVisible(true);
  };

  // When material changes, auto-set unit for paper
  const handleMaterialChange = (materialId) => {
    const mat = materials.find((m) => m.id === materialId);
    if (mat?.category === "PAPER") {
      form.setFieldsValue({ unit: "reel" });
    } else {
      form.setFieldsValue({ unit: "kg", actual_reel_weight: undefined, reel_width: undefined, reel_number: undefined });
    }
  };

  const handleReceive = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // For paper reels, compute weight from actual or empirical formula
      let computedWeight = values.original_weight || values.quantity;
      const mat = materials.find((m) => m.id === values.material_id);
      if (mat?.category === "PAPER" && values.reel_width) {
        const empWeight = values.reel_width * 14;
        const perReel = values.actual_reel_weight || empWeight;
        computedWeight = perReel * (values.quantity || 1);
      }

      const payload = {
        material_id: values.material_id,
        quantity: values.quantity,
        unit: values.unit,
        batch_no: values.batch_no || undefined,
        lot_no: values.lot_no || undefined,
        purchase_rate: values.purchase_rate || undefined,
        location_name: values.location_name || undefined,
        original_weight: computedWeight,
        // Paper-specific fields
        reel_width: values.reel_width || undefined,
        reel_number: values.reel_number || undefined,
        actual_reel_weight: values.actual_reel_weight || undefined,
        empirical_weight: values.reel_width ? values.reel_width * 14 : undefined,
      };

      // Include optional GRN ref if provided
      if (values.grn_number) {
        payload.grn_number = values.grn_number;
      }

      await receiveStock(payload);
      toast.success("Stock received successfully");
      setModalVisible(false);
      fetchStock();
      fetchMovements(1);
    } catch (error) {
      if (error?.errorFields) return;
      console.error(error.message);
      toast.error("Failed to receive stock");
    } finally {
      setSaving(false);
    }
  };

  // ── Stock summary columns ──────────────────────────────────
  const stockColumns = [
    {
      title: "Material",
      key: "material",
      render: (_, record) => (
        <div>
          <Text strong>{record.material?.name || "—"}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.material?.code || ""}
          </Text>
        </div>
      ),
    },
    {
      title: "Reel Width",
      key: "reel_width",
      width: 100,
      render: (_, record) =>
        record.reel_width ? <Text strong>{record.reel_width}"</Text> : "—",
    },
    {
      title: "Category",
      key: "category",
      width: 100,
      render: (_, record) => <Tag>{record.material?.category || "—"}</Tag>,
    },
    {
      title: "Qty (Reels)",
      dataIndex: "total_qty",
      key: "total_qty",
      width: 100,
      render: (val, record) => (
        <Text strong>
          {val ?? 0} {record.reel_width ? "reel" : (record.material?.unit || "")}
        </Text>
      ),
    },
    {
      title: "Total Weight",
      key: "total_weight",
      width: 120,
      render: (_, record) => {
        if (record.total_weight) {
          return <Text strong>{record.total_weight.toFixed(1)} kg</Text>;
        }
        return <Text>{record.total_qty ?? 0} {record.material?.unit || ""}</Text>;
      },
    },
    {
      title: "Available",
      key: "available",
      width: 120,
      render: (_, record) => {
        if (record.available_weight) {
          return (
            <Text style={{ color: record.available_weight <= 0 ? "red" : "green" }}>
              {record.available_weight.toFixed(1)} kg
            </Text>
          );
        }
        return (
          <Text style={{ color: (record.available_qty ?? 0) <= 0 ? "red" : "green" }}>
            {record.available_qty ?? 0} {record.material?.unit || ""}
          </Text>
        );
      },
    },
    {
      title: "Reserved",
      dataIndex: "reserved_qty",
      key: "reserved_qty",
      width: 90,
      render: (val) => val ?? 0,
    },
    {
      title: "Batches",
      dataIndex: "batches",
      key: "batches",
      width: 70,
      render: (batches) => (batches?.length || 0),
    },
  ];

  // ── Recent receipts columns ────────────────────────────────
  const movColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 100,
      render: (val) => (val ? moment(val).format("DD-MM-YYYY") : "—"),
    },
    {
      title: "Material",
      key: "material",
      ellipsis: true,
      render: (_, record) => (
        <div>
          <Text strong>{record.material?.name || "—"}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.material?.code || ""}
          </Text>
        </div>
      ),
    },
    {
      title: "Reel Width",
      key: "reel_width",
      width: 90,
      render: (_, record) =>
        record.stock?.reel_width ? <Text strong>{record.stock.reel_width}"</Text> : "—",
    },
    {
      title: "Category",
      key: "category",
      width: 90,
      render: (_, record) => <Tag>{record.material?.category || "—"}</Tag>,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (val, record) => (
        <Text strong style={{ color: "green" }}>
          +{val ?? 0} {record.unit || ""}
        </Text>
      ),
    },
    {
      title: "Total Weight",
      key: "weight",
      width: 110,
      render: (_, record) => {
        if (record.stock?.original_weight) {
          return <Text strong>{record.stock.original_weight.toFixed(1)} kg</Text>;
        }
        return "—";
      },
    },
    {
      title: "Batch",
      dataIndex: "batch_no",
      key: "batch_no",
      width: 110,
      render: (val) => val || "—",
    },
    {
      title: "Location",
      dataIndex: "to_location",
      key: "to_location",
      width: 120,
      ellipsis: true,
      render: (val) => val || "—",
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      render: (val) => val || "—",
    },
  ];

  // ── Render ─────────────────────────────────────────────────
  return (
    <div>
      <PageTitle title="Back" subtitle="Add Inventory" />

      {/* Current stock summary */}
      <Card
        title={
          <Space>
            <InboxOutlined />
            <span>Current Stock</span>
            <Tag>{stockData.length} materials</Tag>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
            Receive Stock
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8} md={6}>
            <Select
              placeholder="Filter by category"
              allowClear
              style={{ width: "100%" }}
              value={filterCategory}
              onChange={(val) => setFilterCategory(val)}
            >
              <Option value="PAPER">Paper</Option>
              <Option value="GUM_CORR">Gum (Corrugation)</Option>
              <Option value="GUM_PASTE">Gum (Pasting)</Option>
              <Option value="INK">Ink</Option>
              <Option value="WIRE">Wire</Option>
              <Option value="SUTLI">Sutli</Option>
              <Option value="PLATE">Plate</Option>
              <Option value="DIE">Die</Option>
              <Option value="FILM">Film</Option>
              <Option value="STRAP">Strap</Option>
            </Select>
          </Col>
          <Col>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchStock} />
            </Tooltip>
          </Col>
        </Row>

        <Table
          columns={stockColumns}
          dataSource={stockData}
          rowKey={(r) => `${r.material?.id}_${r.reel_width || "all"}`}
          loading={stockLoading}
          pagination={false}
          scroll={{ x: 700 }}
          size="small"
        />
      </Card>

      {/* Recent receipt movements */}
      <Card
        title={
          <Space>
            <span>Recent Stock Receipts</span>
            <Tag>{movPagination.total} total</Tag>
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
            />
          </Col>
          <Col>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={() => fetchMovements(1)} />
            </Tooltip>
          </Col>
        </Row>

        <Table
          columns={movColumns}
          dataSource={movements}
          rowKey={(r) => r.id || Math.random()}
          loading={movLoading}
          pagination={{
            ...movPagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} receipts`,
          }}
          onChange={(pag) => fetchMovements(pag.current, pag.pageSize)}
          scroll={{ x: 800 }}
          size="small"
        />
      </Card>

      {/* ── Receive Stock Modal ────────────────────────────── */}
      <Modal
        title="Receive Stock"
        open={modalVisible}
        onOk={handleReceive}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        width={650}
        okText="Receive Stock"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="material_id"
                label="Raw Material"
                rules={[{ required: true, message: "Select a material" }]}
              >
                <Select
                  showSearch
                  loading={matLoading}
                  placeholder="Select material..."
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleMaterialChange}
                >
                  {materials.map((m) => (
                    <Option key={m.id} value={m.id}>
                      {m.code ? `${m.code} — ${m.name}` : m.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unit" label="Unit" rules={[{ required: true }]}>
                <Select>
                  <Option value="kg">Kg</Option>
                  <Option value="reel">Reel</Option>
                  <Option value="liters">Litres</Option>
                  <Option value="meters">Metres</Option>
                  <Option value="pieces">Pieces</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* ── Non-paper: Quantity & Rate ── */}
          {!isPaperMaterial && (
            <>
              <Divider style={{ margin: "8px 0 16px" }}>Quantity & Rate</Divider>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="quantity"
                    label="Quantity"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber min={0.01} step={0.01} style={{ width: "100%" }} placeholder="1000" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="purchase_rate" label="Purchase Rate (₹/kg)">
                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="45.00" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="original_weight" label="Original Weight">
                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="Same as qty" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* ── Paper/Reel: tabular entry ── */}
          {isPaperMaterial && (
            <>
              <Divider style={{ margin: "8px 0 16px" }}>Reel Details</Divider>

              <Form.Item name="reel_number" label="Reel Number" style={{ marginBottom: 12 }}>
                <Input placeholder="e.g. R001" style={{ width: 200 }} />
              </Form.Item>

              <Form.Item name="purchase_rate" label="Purchase Rate (₹/kg)" style={{ marginBottom: 12 }}>
                <InputNumber min={0} step={0.01} style={{ width: 200 }} placeholder="45.00" />
              </Form.Item>

              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
                <thead>
                  <tr style={{ background: "#fafafa", textAlign: "left" }}>
                    <th style={{ padding: "8px 12px", border: "1px solid #f0f0f0", width: "25%" }}>Reel Width (in)</th>
                    <th style={{ padding: "8px 12px", border: "1px solid #f0f0f0", width: "20%" }}>Quantity</th>
                    <th style={{ padding: "8px 12px", border: "1px solid #f0f0f0", width: "25%" }}>Weight / Reel (kg)</th>
                    <th style={{ padding: "8px 12px", border: "1px solid #f0f0f0", width: "30%" }}>Net Weight (kg)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: "8px 12px", border: "1px solid #f0f0f0" }}>
                      <Form.Item name="reel_width" noStyle rules={[{ required: true, message: "Required" }]}>
                        <InputNumber min={1} max={200} style={{ width: "100%" }} placeholder="34" />
                      </Form.Item>
                    </td>
                    <td style={{ padding: "8px 12px", border: "1px solid #f0f0f0" }}>
                      <Form.Item name="quantity" noStyle rules={[{ required: true, message: "Required" }]}>
                        <InputNumber min={1} step={1} style={{ width: "100%" }} placeholder="1" />
                      </Form.Item>
                    </td>
                    <td style={{ padding: "8px 12px", border: "1px solid #f0f0f0" }}>
                      <Form.Item name="actual_reel_weight" noStyle>
                        <InputNumber
                          min={0}
                          step={0.01}
                          style={{ width: "100%" }}
                          placeholder={empiricalWeightPerReel ? `≈ ${empiricalWeightPerReel}` : "auto"}
                        />
                      </Form.Item>
                    </td>
                    <td style={{ padding: "8px 12px", border: "1px solid #f0f0f0", background: totalReelWeight ? "#f6ffed" : "transparent" }}>
                      <Text strong style={{ fontSize: 14, color: totalReelWeight ? "#389e0d" : undefined }}>
                        {totalReelWeight ? `${totalReelWeight} kg` : "—"}
                      </Text>
                    </td>
                  </tr>
                </tbody>
              </table>

              {watchReelWidth > 0 && (
                <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 6, padding: "8px 12px", marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Empirical weight per reel: <Text strong>{empiricalWeightPerReel} kg</Text> (width {watchReelWidth}" × 14)
                  </Text>
                  {watchActualReelWeight ? (
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
                      | Using <Text strong>actual: {watchActualReelWeight} kg</Text>/reel
                    </Text>
                  ) : null}
                  {watchQuantity > 0 && (
                    <Text style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                      Total weight ({watchQuantity} reel{watchQuantity > 1 ? "s" : ""}): <Text strong style={{ color: "#389e0d" }}>{totalReelWeight} kg</Text>
                    </Text>
                  )}
                </div>
              )}
            </>
          )}

          <Divider style={{ margin: "8px 0 16px" }}>Batch & Location</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="batch_no" label="Batch No.">
                <Input placeholder="e.g. BATCH-2026-001" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="lot_no" label="Lot No.">
                <Input placeholder="e.g. LOT-001" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="grn_number" label="GRN No. (optional)">
                <Input placeholder="e.g. GRN-202603-0001" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="location_name" label="Storage Location">
            <Input placeholder="e.g. Warehouse A, Rack 3" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QuickStockEntry;
